import { ArrowLeft, UserCog, AlertCircle } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { UserForm } from '@/components/features/users/UserForm'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  useUser,
  useUpdateUser,
  useUserRoles,
  useAssignRoles,
  useRemoveRoles,
  useRealmRoles,
} from '@/hooks/operations/use-users'
import { type UpdateUserFormData } from '@/lib/validation/user-schemas'
import {
  type KeycloakUser,
  type UpdateUserRequest,
  type KeycloakRole,
  type UserAttributes,
} from '@/models/domain/keycloak-user'

function ensureArray(value: unknown): string[] {
  if (Array.isArray(value)) return value as string[]
  return []
}

function boolToAttr(value: boolean | undefined): string[] {
  return value ? ['true'] : ['false']
}

function buildUserAttributes(formData: UpdateUserFormData): Partial<UserAttributes> {
  return {
    system: ensureArray(formData.attributes.systems),
    systems_all: boolToAttr(formData.attributes.systems_all),
    dba: ensureArray(formData.attributes.dbas),
    district: ensureArray(formData.attributes.districts),
    location: ensureArray(formData.attributes.locations),
    locations_all: boolToAttr(formData.attributes.locations_all),
    section: ensureArray(formData.attributes.sections),
    sections_all: boolToAttr(formData.attributes.sections_all),
    locked: boolToAttr(formData.accountStatus.locked),
  }
}

function buildUpdateRequest(formData: UpdateUserFormData): UpdateUserRequest {
  const request: UpdateUserRequest = {
    email: formData.basicInfo.email,
    firstName: formData.basicInfo.firstName,
    lastName: formData.basicInfo.lastName,
    enabled: formData.accountStatus.enabled,
    emailVerified: formData.accountStatus.emailVerified || false,
    clientRoles: formData.clientRoles,
    attributes: buildUserAttributes(formData),
  }

  if (formData.basicInfo.password && formData.basicInfo.password.length > 0) {
    request.credentials = [
      {
        type: 'password',
        value: formData.basicInfo.password,
        temporary: false,
      },
    ]
  }

  return request
}

function computeRoleChanges(
  formData: UpdateUserFormData,
  currentUserRoles: KeycloakRole[]
): { rolesToAdd: string[]; rolesToRemove: string[] } {
  const managedRoleNames = Object.keys(formData.realmRoles)
  const currentManagedRoles = currentUserRoles
    .filter((role) => managedRoleNames.includes(role.name))
    .map((role) => role.name)
  const selectedRoleNames = Object.entries(formData.realmRoles)
    .filter(([_key, value]) => value)
    .map(([key]) => key)

  return {
    rolesToAdd: selectedRoleNames.filter((name) => !currentManagedRoles.includes(name)),
    rolesToRemove: currentManagedRoles.filter((name) => !selectedRoleNames.includes(name)),
  }
}

function attrIsTrue(values: string[] | undefined): boolean {
  return values?.[0] === 'true'
}

function buildRealmRolesDefaults(roleNames: string[]) {
  const MANAGED_ROLES = [
    'cs_sys_op',
    'cs_sys_admin',
    'cs_active',
    'cs_locked',
    'cs_systems_all',
    'cs_locations_all',
    'cs_sections_all',
  ] as const

  const result: Record<string, boolean> = {}
  for (const role of MANAGED_ROLES) {
    // eslint-disable-next-line security/detect-object-injection
    result[role] = roleNames.includes(role)
  }
  return result
}

interface AttributeDefaults {
  systems: string[]
  systems_all: boolean
  dbas: string[]
  districts: string[]
  locations: string[]
  locations_all: boolean
  sections: string[]
  sections_all: boolean
}

interface AttributeGroup {
  plural: keyof AttributeDefaults
  attrKey: keyof UserAttributes
  allFlag?: keyof AttributeDefaults
  allAttrKey?: keyof UserAttributes
}

const ATTRIBUTE_GROUPS: readonly AttributeGroup[] = [
  { plural: 'systems', allFlag: 'systems_all', attrKey: 'system', allAttrKey: 'systems_all' },
  { plural: 'dbas', attrKey: 'dba' },
  { plural: 'districts', attrKey: 'district' },
  {
    plural: 'locations',
    allFlag: 'locations_all',
    attrKey: 'location',
    allAttrKey: 'locations_all',
  },
  { plural: 'sections', allFlag: 'sections_all', attrKey: 'section', allAttrKey: 'sections_all' },
]

function buildAttributeDefaults(user: KeycloakUser): AttributeDefaults {
  const attrs = user.attributes
  const result: Record<string, string[] | boolean> = {}
  for (const group of ATTRIBUTE_GROUPS) {
    result[group.plural] = attrs?.[group.attrKey] ?? []
    if (group.allFlag && group.allAttrKey) {
      result[group.allFlag] = attrIsTrue(attrs?.[group.allAttrKey])
    }
  }
  return result as unknown as AttributeDefaults
}

function buildDefaultValues(user: KeycloakUser, roleNames: string[]): UpdateUserFormData {
  return {
    basicInfo: {
      email: user.email || '',
      username: user.username || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      password: '',
    },
    accountStatus: {
      enabled: user.enabled,
      emailVerified: user.emailVerified,
      locked: attrIsTrue(user.attributes?.locked),
    },
    realmRoles: buildRealmRolesDefaults(roleNames),
    clientRoles: user.clientRoles ?? {},
    attributes: buildAttributeDefaults(user),
  }
}

function EditUserLoadingView() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <LoadingSpinner size="lg" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading user data...</p>
      </div>
    </div>
  )
}

function EditUserErrorView({
  fetchError,
  onBack,
}: {
  fetchError: Error | null
  onBack: () => void
}) {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to users
      </button>

      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
              Error loading user
            </h3>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300">
              {fetchError ? 'Failed to load user data. Please try again.' : 'User not found.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function EditUserHeader({ username, onBack }: { username: string; onBack: () => void }) {
  return (
    <div className="mb-6">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to users
      </button>
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
          <UserCog className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit User</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Update settings for {username}</p>
        </div>
      </div>
    </div>
  )
}

interface ApplyRoleChangesParams {
  id: string
  rolesToAdd: string[]
  rolesToRemove: string[]
  realmRoles: KeycloakRole[] | undefined
  userRoles: KeycloakRole[]
  assignRolesMutation: {
    mutateAsync: (args: { id: string; roles: KeycloakRole[] }) => Promise<unknown>
  }
  removeRolesMutation: {
    mutateAsync: (args: { id: string; roles: KeycloakRole[] }) => Promise<unknown>
  }
}

async function applyRoleChanges(params: ApplyRoleChangesParams) {
  const {
    id,
    rolesToAdd,
    rolesToRemove,
    realmRoles,
    userRoles,
    assignRolesMutation,
    removeRolesMutation,
  } = params

  if (rolesToAdd.length > 0 && realmRoles) {
    const roleObjects: KeycloakRole[] = rolesToAdd
      .map((name) => realmRoles.find((role) => role.name === name))
      .filter((role): role is KeycloakRole => role !== undefined)

    if (roleObjects.length > 0) {
      await assignRolesMutation.mutateAsync({ id, roles: roleObjects })
    }
  }

  if (rolesToRemove.length > 0) {
    const existingRoles = userRoles.filter((role) => rolesToRemove.includes(role.name))
    await removeRolesMutation.mutateAsync({ id, roles: existingRoles })
  }
}

export function EditUserPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  const { data: user, isLoading: isLoadingUser, error: fetchError } = useUser(id)
  const { data: userRoles, isLoading: isLoadingRoles } = useUserRoles(id)
  const { data: realmRoles } = useRealmRoles()
  const updateUserMutation = useUpdateUser()
  const assignRolesMutation = useAssignRoles()
  const removeRolesMutation = useRemoveRoles()

  const handleSubmit = async (formData: UpdateUserFormData) => {
    if (!id || !userRoles) return
    setError(null)

    try {
      await updateUserMutation.mutateAsync({ id, request: buildUpdateRequest(formData) })
      const { rolesToAdd, rolesToRemove } = computeRoleChanges(formData, userRoles)
      await applyRoleChanges({
        id,
        rolesToAdd,
        rolesToRemove,
        realmRoles,
        userRoles,
        assignRolesMutation,
        removeRolesMutation,
      })
      navigate('/operations/users', {
        state: { success: `User ${formData.basicInfo.username} updated successfully!` },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user')
    }
  }

  if (isLoadingUser || isLoadingRoles) {
    return <EditUserLoadingView />
  }

  if (fetchError || !user) {
    return (
      <EditUserErrorView
        fetchError={fetchError}
        onBack={() => {
          navigate('/operations/users')
        }}
      />
    )
  }

  const roleNames = userRoles?.map((role) => role.name) ?? []

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <EditUserHeader
        username={user.username}
        onBack={() => {
          navigate('/operations/users')
        }}
      />
      <UserForm
        mode="edit"
        defaultValues={buildDefaultValues(user, roleNames)}
        onSubmit={handleSubmit}
        isLoading={updateUserMutation.isPending}
        error={error}
      />
    </div>
  )
}
